import { HierarchyAccessError, id, loadHierarchy, object, request, session, type HierarchyCatalog, type HierarchyThread } from "./api";
import { historicalEvidence, requestTurn, type HistoricalTurn } from "./history";
import { withApiRequestScope } from "../lib/api";
import { accountHistoryThread, authorizeAccountHistory } from "./account-history";

export interface TranscriptSelection {
  accountHistory?: true;
  accountId: string;
  workspaceId: string;
  projectId?: string;
  threadId: string;
}

export interface DocumentReference { artifactId: string; versionId: string }
export interface TranscriptTurn {
  legacy?: HistoricalTurn;
  stateless?: boolean;
  id: string;
  accountId: string;
  workspaceId: string;
  threadId: string;
  projectId?: string;
  parentId?: string;
  input: string;
  reply: string;
  outcome: string;
  inputs: DocumentReference[];
  outputs: DocumentReference[];
}
export interface TranscriptPage {
  canWrite?: boolean;
  catalog: HierarchyCatalog;
  thread: HierarchyThread;
  turns: TranscriptTurn[];
  nextNode?: string;
}

function optionalId(value: unknown): string | undefined {
  return value === undefined || value === "" ? undefined : id(value);
}

function references(value: unknown): DocumentReference[] {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value) || value.length > 32) throw new HierarchyAccessError();
  return value.map((raw) => {
    const ref = object(raw);
    return { artifactId: id(ref.artifact_id), versionId: id(ref.version_id) };
  });
}

function parseTurn(raw: Record<string, unknown>, node: string, selection: TranscriptSelection, evidenceJSON: string): TranscriptTurn {
  const scope = object(raw.scope);
  const input = object(raw.input);
  const result = object(raw.result);
  const legacy = historicalEvidence(raw, evidenceJSON);
  if (raw.id !== node || (!legacy && scope.run_id !== node) || scope.account_id !== selection.accountId || scope.workspace_id !== selection.workspaceId ||
    typeof input.text !== "string" || typeof result.reply !== "string" ||
    !["completed", "failed", "cancelled", "waiting_for_user", "needs_more_credits"].includes(String(result.outcome))) {
    throw new HierarchyAccessError();
  }
  if (selection.accountHistory && (!legacy || scope.thread_id !== selection.threadId || optionalId(scope.project_id))) throw new HierarchyAccessError();
  // Inherited turns retain their original thread/project. The requested thread's
  // server endpoint authorizes the exact ancestry before returning each node.
  return {
    id: node, accountId: selection.accountId, workspaceId: selection.workspaceId,
    threadId: id(scope.thread_id), projectId: optionalId(scope.project_id), parentId: optionalId(raw.parent_id),
    input: input.text, reply: result.reply, outcome: String(result.outcome),
    inputs: references(input.artifacts), outputs: references(result.artifacts),
    stateless: raw.stateless === true,
    legacy,
  };
}

export async function loadTranscript(selection: TranscriptSelection, previous?: TranscriptPage, signal?: AbortSignal): Promise<TranscriptPage> {
  return withApiRequestScope((active) => readTranscript(selection, previous, active), signal);
}

async function readTranscript(selection: TranscriptSelection, previous: TranscriptPage | undefined, signal: AbortSignal): Promise<TranscriptPage> {
  id(selection.accountId); id(selection.threadId);
  if (selection.accountHistory) {
    if (selection.workspaceId !== "" || selection.projectId !== undefined) throw new HierarchyAccessError();
  } else { id(selection.workspaceId); }
  if (selection.projectId !== undefined) id(selection.projectId);
  const path = selection.accountHistory
    ? `/code/account-threads/${encodeURIComponent(selection.threadId)}`
    : `/code/workspaces/${encodeURIComponent(selection.workspaceId)}/threads/${encodeURIComponent(selection.threadId)}`;
  let catalog: HierarchyCatalog;
  let current: Record<string, unknown> | undefined;
  if (selection.accountHistory) {
    const scope = await authorizeAccountHistory(selection.accountId, signal, previous?.catalog.scope);
    current = await request(path, signal);
    const thread = accountHistoryThread(current, selection.accountId, selection.threadId);
    catalog = { scope, workspaces: [], projects: [], threads: [thread] };
  } else {
    catalog = await loadHierarchy(selection, signal);
  }
  const thread = catalog.threads.find((row) => row.id === selection.threadId);
  if (!thread) throw new HierarchyAccessError();
  if (previous && (previous.catalog.scope.accountId !== catalog.scope.accountId || previous.catalog.scope.userId !== catalog.scope.userId ||
    previous.thread.id !== thread.id || previous.thread.workspaceId !== thread.workspaceId || previous.thread.projectId !== thread.projectId)) throw new HierarchyAccessError();
  current ??= await request(path, signal);
  if (current.id !== thread.id || current.accountId !== thread.accountId || current.workspaceId !== thread.workspaceId || optionalId(current.projectId) !== thread.projectId) throw new HierarchyAccessError();
  let node = previous ? previous.nextNode : optionalId(current.headNodeId);
  const seen = new Set(previous?.turns.map((turn) => turn.id));
  const loaded: TranscriptTurn[] = [];
  while (node && loaded.length < 20) {
    if (seen.has(node)) throw new HierarchyAccessError();
    seen.add(node);
    const evidence = await requestTurn(`${path}/turns/${encodeURIComponent(node)}`, signal);
    const turn = parseTurn(evidence.body, node, selection, evidence.evidenceJSON);
    loaded.push(turn);
    node = turn.parentId;
  }
  if (node && seen.has(node)) throw new HierarchyAccessError();
  // Reauthorize the oldest node before exposing this batch. A revoked ancestor
  // invalidates all previously loaded pages, not just the newly fetched nodes.
  const oldest = loaded.at(-1)?.id ?? previous?.turns[0]?.id;
  if (oldest) {
    const evidence = await requestTurn(`${path}/turns/${encodeURIComponent(oldest)}`, signal);
    parseTurn(evidence.body, oldest, selection, evidence.evidenceJSON);
  }
  const identity = await session(signal);
  if (identity.accountId !== catalog.scope.accountId || identity.userId !== catalog.scope.userId) throw new HierarchyAccessError();
  if (selection.accountHistory) {
    if (identity.role !== "owner" && identity.role !== "manager") throw new HierarchyAccessError();
    accountHistoryThread(await request(path, signal), selection.accountId, selection.threadId);
  }
  signal?.throwIfAborted();
  // Missing/unknown permission metadata is read-only, including older servers.
  // The server independently rechecks authority for every mutation.
  const canWrite = !selection.accountHistory && (current.accessRole === "admin" || current.accessRole === "editor");
  return { catalog, thread, canWrite, turns: [...loaded.reverse(), ...(previous?.turns ?? [])], nextNode: node };
}
